/**
 * SettingsActions — pure handler factories for SettingsBody.
 *
 * All side-effects (localStorage, DOM, i18n language, toast, server-sync
 * debounce) live here so SettingsBody stays declarative.
 */

// ── localStorage keys ──────────────────────────────────────────────────────────
export const LS_THEME = 'sagwa.settings.theme'
export const LS_HAPTIC = 'sagwa.settings.haptic'
export const LS_LANG = 'sagwa.settings.lang'

// Anonymous mode removed — no privacy story for the W1 audience, see commit
// removing the row from SettingsBody for rationale.

export type Theme = 'auto' | 'light' | 'dark'

// ── localStorage helpers ───────────────────────────────────────────────────────

export function lsGet(key: string, fallback: string): string {
  try {
    return window.localStorage.getItem(key) ?? fallback
  } catch {
    return fallback
  }
}

export function lsSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* quota / sandbox — in-memory state still correct for this session */
  }
}

export function lsBool(key: string, fallback: boolean): boolean {
  const raw = lsGet(key, fallback ? '1' : '0')
  return raw === '1'
}

// ── DOM theme application ──────────────────────────────────────────────────────

/**
 * Per-CSS-variable list that useTelegramTheme.applyTelegramTheme() sets as
 * INLINE styles on :root. Inline styles outrank `[data-theme="dark"]` CSS
 * selectors, so manual theme override has to clear them. Kept in lockstep
 * with applyTelegramTheme(); add new vars to both lists.
 */
const TG_INLINE_VARS = [
  '--tg-bg-color',
  '--tg-text-color',
  '--tg-hint-color',
  '--tg-link-color',
  '--tg-button-color',
  '--tg-button-text-color',
  '--tg-secondary-bg-color',
  '--tg-header-bg-color',
] as const

export function applyThemeToDom(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'auto') {
    root.removeAttribute('data-theme')
    // Re-apply Telegram themeParams. Without this, the user might toggle
    // light → auto and see a previously-cleared inline var stay cleared,
    // making "Auto" effectively render as the global.css :root defaults
    // instead of TG's themeParams.
    const params = window.Telegram?.WebApp?.themeParams
    if (params) {
      // Imported lazily to avoid a circular dep between SettingsActions
      // and useTelegramTheme.
      void import('../../hooks/useTelegramTheme').then(({ applyTelegramTheme }) => {
        applyTelegramTheme(params)
      })
    }
  } else {
    root.setAttribute('data-theme', theme)
    // Clear inline TG-set CSS vars so the [data-theme="dark"] or
    // [data-theme="light"] selector rules in global.css can win.
    for (const v of TG_INLINE_VARS) {
      root.style.removeProperty(v)
    }
  }
}

// ── Haptic preference ──────────────────────────────────────────────────────────

/**
 * Read the haptic preference. Default ON. Used by every component that
 * fires Telegram.WebApp.HapticFeedback so the Settings toggle has actual
 * effect (instead of being a localStorage-only no-op).
 */
export function isHapticEnabled(): boolean {
  return lsBool(LS_HAPTIC, true)
}

/**
 * Fire a Telegram WebApp haptic feedback call, gated by the user's
 * preference. Centralizes the optional-chain pattern + preference check.
 */
export function fireHaptic(
  kind: 'success' | 'warning' | 'error',
): void {
  if (!isHapticEnabled()) return
  const tg = window.Telegram?.WebApp
  tg?.HapticFeedback?.notificationOccurred?.(kind)
}
